/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Card,
  CardMedia,
  Grid,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";

import { InfoBoxMain } from "client/views/InfoBox/InfoBoxMain";
import { MyNotes } from "./StudyNotes";
import { backButton } from "components/Form/FormField";
import { initScreen } from "./studyUtils";
import { progressCircles } from "components/Form/FormField";
import { sMyStudy, sStudyEnroll, sMyNotes } from "assets/jss/views/ResStrings";
import { Modal } from "components/Modal/Modal";

import { getCourses, getImage, ApricotAction } from "client/actions/courses";
import { getStudyItems } from "client/actions/own";
import { setScreenInfo } from "client/actions/modal";

import { AllOwns } from "client/reducers/ownReducer";
import { StudyState } from "client/reducers/studyReducer";
import { ScreenInfo } from "client/reducers/modalReducer";
import { selectorCourses } from "client/selectors/courseSelector";
import { selectorOwns } from "client/selectors/ownSelector";
import { selectorStudy } from "client//selectors/studySelector";

import studyPageStyle from "assets/jss/views/studyPage/studyPageStyle.jsx";
const styles = createStyles(studyPageStyle as any);

interface Props extends WithStyles<typeof styles> {
  getCourses: ApricotAction;
  getImage: ApricotAction;
  getStudyItems: ApricotAction;
  setScreenInfo: ApricotAction;
  // store data
  courses: any[];
  courseImages: any;
  owns: AllOwns;
  uid: string;
  study: StudyState;
  screenInfo: ScreenInfo;
  // component props
  history: any;
}
const StudyMainBase: React.FC<Props> = ({
  getCourses,
  getImage,
  getStudyItems,
  setScreenInfo,
  courses,
  courseImages,
  owns,
  uid,
  study,
  screenInfo,
  history,
  classes,
}: Props) => {
  const [activeType, setActiveType] = useState(-1);
  const [selectedIBox, setSelectedIBox] = useState<string | null>(null);
  const [notesOpen, setNotesOpen] = useState(false);

  const refSelectedCourse = useRef<string | null>(null);
  const theme = useTheme();

  useEffect(() => {
    getCourses();

    const { screenEvt, screenEvtHandler } = initScreen(setScreenInfo);
    return () => {
      window.removeEventListener(screenEvt, screenEvtHandler);
    };
  }, []);

  useEffect(() => {
    if (activeType >= 0 && activeType <= 4) {
      getStudyItems(uid, activeType);
    }
  }, [activeType]);

  const typeLabel = [
    "Lectures",
    "Handouts",
    "Cards",
    "Tests",
    "Assignments",
    sMyNotes,
  ];

  const typeColors = [
    theme.palette.primary.main,
    theme.palette.info.main,
    theme.palette.secondary.light,
    theme.palette.test.main,
    theme.palette.warning.dark,
    theme.palette.notes.main,
  ];

  const renderStudyList = () => {
    const card = (idx: number) => (
      <div
        key={idx}
        className={classes.listItem}
        style={{ backgroundColor: `${typeColors[idx]}` }}
        onClick={() => setActiveType(idx)}>
        {typeLabel[idx]}
      </div>
    );

    return (
      <>
        <div className={classes.studyListV}>
          {typeLabel.map((label: any, idx: number) => card(idx))}
        </div>
        <div className={classes.studyListH}>
          <Grid container spacing={6} style={{ marginTop: "4rem" }}>
            {typeLabel.map((label: any, idx: number) => (
              <Grid item key={idx} xs={4} style={{ padding: "0 0.5rem" }}>
                {card(idx)}
              </Grid>
            ))}
          </Grid>
        </div>
      </>
    );
  };

  const renderCourses = () => {
    const fromStore = activeType !== 5;
    const typeStore = [
      "studyLecs",
      "studyHdo",
      "studyCards",
      "studyTests",
      "studyAsm",
    ];
    const studyItems = fromStore ? study[typeStore[activeType]] : owns;

    if (!studyItems) {
      return progressCircles();
    }
    if (!Object.keys(studyItems).length) {
      return (
        <div
          style={{
            fontSize: "1.5rem",
            width: "100%",
            textAlign: "center",
            marginTop: "5rem",
          }}>
          There is no any item in this category.
        </div>
      );
    }

    let ret: Array<React.ReactElement> = [];
    Object.keys(owns).forEach((cid: string) => {
      if (studyItems[cid]) {
        const courseIdx = courses.findIndex((crs) => crs._id === cid);
        if (courseIdx !== -1) {
          const { title, imageLink } = courses[courseIdx];

          if (!courseImages[cid]) {
            getImage({ cid, imgPath: imageLink });
          }

          ret.push(
            <div key={cid}>
              <Card
                className={classes.courseCard}
                style={{
                  cursor: "pointer",
                  backgroundColor: theme.palette.paper.light,
                }}
                onClick={() => {
                  if (activeType === 5) {
                    refSelectedCourse.current = cid;
                    setNotesOpen(true);
                  } else {
                    history.push(`/crp/${cid}`);
                  }
                }}>
                <CardMedia
                  classes={{ root: classes.cardMedia }}
                  style={{
                    backgroundImage: courseImages[cid]
                      ? `url(data:image/jpeg;base64,${courseImages[cid]})`
                      : "",
                  }}>
                  &nbsp;
                </CardMedia>
                <div className={classes.crsLabel}>{title}</div>
              </Card>

              {fromStore &&
                studyItems[cid].map((data: any, idx: number) => {
                  return (
                    <div key={idx} className={classes.lecLabel}>
                      <div
                        className={classes.lecBullet}
                        style={{ backgroundColor: `${typeColors[activeType]}` }}
                      />
                      {data.sched ? (
                        <div
                          className={classes.lecTitle}
                          style={{ cursor: "default", color: "#B0B0B0" }}>
                          {data.title}
                        </div>
                      ) : (
                        <div
                          className={classes.lecTitle}
                          onClick={() => {
                            refSelectedCourse.current = cid;
                            activeType === 0
                              ? history.push(`/clsrm/${cid}/${data._id}`)
                              : setSelectedIBox(data._id);
                          }}>
                          {data.title}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>,
          );
        }
      }
    });
    return ret;
  };

  const handleClose = () => {
    setActiveType(-1);
    setNotesOpen(false);
  };

  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const renderDialog = () => {
    return (
      <Dialog
        classes={{ paper: classes.studyDlg }}
        fullScreen={fullScreen}
        open={true}
        onClose={handleClose}
        scroll={"paper"}>
        <DialogTitle
          id="scroll-dialog-title"
          classes={{ root: classes.dlgTitle }}
          style={{ backgroundColor: typeColors[activeType] }}>
          <div className={classes.titleText}>
            {typeLabel[activeType]}
            <IconButton color="inherit" onClick={handleClose}>
              <Close />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent>
          <>
            {notesOpen ? (
              <MyNotes
                cid={refSelectedCourse.current}
                history={history}
                onClose={(exitStudy: boolean) =>
                  exitStudy ? handleClose() : setNotesOpen(false)
                }
              />
            ) : (
              renderCourses()
            )}
          </>
        </DialogContent>
      </Dialog>
    );
  };

  const onAction = (params: any) => {
    switch (params.action) {
      case "ibx":
        setSelectedIBox(null);
        break;
    }
  };

  return (
    <>
      {activeType >= 0 && !selectedIBox && renderDialog()}
      {selectedIBox && (
        <InfoBoxMain
          cid={refSelectedCourse.current}
          bid={selectedIBox}
          inStudy={true}
          onAction={(params: any) => onAction(params)}
        />
      )}
      <div className={classes.myStudy}>
        <div className={classes.container}>
          <div className={classes.brand}>
            <h1 className={classes.title}>{sMyStudy}</h1>
          </div>
          {backButton(history)}
          <div className={classes.studyPanel}>{renderStudyList()}</div>
        </div>
      </div>
      {owns
        ? Object.keys(owns).length === 0 && (
            <Modal
              nobackdrop
              title={"Enrollment Required"}
              onExit={() => history.push("/cr_list")}>
              {sStudyEnroll}
            </Modal>
          )
        : progressCircles()}
    </>
  );
};

function mapStateToProps(state: any) {
  return {
    courses: selectorCourses(state),
    courseImages: state.state_course.courseImages,
    owns: selectorOwns(state),
    uid: state.state_auth._id,
    study: selectorStudy(state),
    screenInfo: state.state_modal.screenInfo,
  };
}

function loadData(store: any, params: any) {
  return store.dispatch(getCourses(""));
}

const base: any = StudyMainBase;
export const StudyMain = {
  loadData, // loadData: loadData
  component: withStyles(styles)(
    connect(mapStateToProps, {
      getCourses,
      getImage,
      getStudyItems,
      setScreenInfo,
    })(base),
  ),
};

export const StudyMainTest = withStyles(styles)(base);
